import { motion } from 'framer-motion';
import {
  Brain, Shield, BarChart3, Zap, Users, Lock,
  CheckCircle, ArrowRight, Mail, Calendar,
  Layers, Video, AlertTriangle, Download, Play,
  MessageSquare, Bell, Settings, Globe, Cpu,
  CreditCard, UserPlus, FileText, Eye, Mic,
  RefreshCw, Search, Workflow, MonitorSmartphone
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.5 },
};

const stagger = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, margin: '-40px' },
  variants: { visible: { transition: { staggerChildren: 0.06 } } },
};
const cardV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const Logo = ({ h = 40 }) => (
  <img src="/d3-logo-transparent.png" alt="D3 Command" style={{ height: `${h}px`, width: 'auto' }} />
);

const SectionHeader = ({ tag, title, sub }) => (
  <motion.div {...fadeUp} className="mb-8">
    {tag && (
      <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-400 mb-2">
        {tag}
      </span>
    )}
    <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
    {sub && <p className="text-sm text-zinc-500 mt-1.5 max-w-xl">{sub}</p>}
  </motion.div>
);

const LiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
    <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
    Live
  </span>
);

const FeatureRow = ({ icon: Icon, title, desc, color }) => (
  <motion.div
    variants={cardV}
    className="flex items-start gap-3 p-3.5 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition-colors"
  >
    <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center shrink-0 mt-0.5`}>
      <Icon className="w-4 h-4 text-white/90" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-xs font-semibold text-white">{title}</span>
        <LiveBadge />
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

export default function ProductSheetPage() {
  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#07090F] text-white antialiased" data-testid="product-sheet-page">
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
        }
      `}</style>

      {/* Top Bar */}
      <header className="sticky top-0 z-50 bg-[#07090F]/80 backdrop-blur-xl border-b border-white/5 no-print">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Logo h={32} />
          <div className="flex items-center gap-3">
            <a
              href="/login?demo=true"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/10 border border-emerald-500/20 text-sm text-emerald-400 hover:bg-emerald-600/20 transition-colors"
              data-testid="try-demo-top"
            >
              <Play className="w-4 h-4" />
              Try Live Demo
            </a>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              Save as PDF
            </button>
            <a
              href="mailto:invest@d3command.com"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
              data-testid="contact-cta-top"
            >
              <Mail className="w-4 h-4" />
              Get in Touch
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-20">

        {/* HERO */}
        <section className="relative" data-testid="hero-section">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-indigo-600/8 rounded-full blur-[120px] pointer-events-none" />
          <motion.div {...fadeUp} className="relative text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-medium mb-6">
              <Zap className="w-3 h-3" /> Acquisition Opportunity
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] mb-4">
              AI-Powered Operations &amp; Risk <br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
                Intelligence Platform
              </span>
            </h1>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-8">
              D3 Command turns meetings into execution — automatically extracting tasks, identifying risks,
              and surfacing AI-driven insights. A production-ready SaaS platform built for enterprise deployment.
            </p>
            <a
              href="/login?demo=true"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-500 transition-all hover:shadow-lg hover:shadow-indigo-600/20"
              data-testid="hero-demo-btn"
            >
              <Play className="w-4 h-4" />
              Experience the Live Product
            </a>
          </motion.div>

          {/* Capability Stats */}
          <motion.div
            {...stagger}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
            data-testid="key-metrics"
          >
            {[
              { value: '28+', label: 'Shipped Features', note: 'All production-ready & tested' },
              { value: '3', label: 'Native Integrations', note: 'Zoom, Google Meet, Teams' },
              { value: '4-Tier', label: 'Billing Infrastructure', note: 'Free to Enterprise' },
              { value: 'Full', label: 'Enterprise Hardening', note: 'Auth, RBAC, Audit, Rate Limiting' },
            ].map(s => (
              <motion.div key={s.label} variants={cardV} className="text-center">
                <p className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{s.value}</p>
                <p className="text-xs sm:text-sm text-zinc-500 mt-1 font-medium">{s.label}</p>
                <p className="text-[10px] text-zinc-700 mt-0.5 italic">{s.note}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* TRUST SIGNAL */}
        <motion.div
          {...fadeUp}
          className="flex items-center justify-center gap-3 px-5 py-3 rounded-xl bg-emerald-500/[0.05] border border-emerald-500/10"
          data-testid="trust-signal"
        >
          <Shield className="w-4 h-4 text-emerald-500 shrink-0" />
          <p className="text-xs text-zinc-400">
            <span className="text-emerald-400 font-medium">Production-ready platform</span> — not a prototype. Every feature below is live, tested, and ready for enterprise deployment. Try the demo to verify.
          </p>
        </motion.div>

        {/* VALUE CHAIN */}
        <section data-testid="value-chain-section">
          <SectionHeader
            tag="How It Works"
            title="From Conversations to Execution & Risk Intelligence"
            sub="D3 Command isn't meeting transcription — it's a full operational intelligence pipeline."
          />
          <motion.div {...fadeUp} className="relative p-6 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { step: '01', label: 'Meetings', desc: 'Ingest recordings from Zoom, Google Meet, Teams, or email', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                { step: '02', label: 'Tasks', desc: 'AI extracts action items with owners, deadlines, and priorities', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
                { step: '03', label: 'Risks', desc: 'Automatically identify blockers, compliance gaps, and concerns', color: 'text-amber-400', bg: 'bg-amber-500/10' },
                { step: '04', label: 'Insights', desc: 'AI-generated patterns, alerts, and productivity recommendations', color: 'text-violet-400', bg: 'bg-violet-500/10' },
                { step: '05', label: 'Tracking', desc: 'Real-time dashboards, audit trails, and team accountability', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              ].map((s, i) => (
                <div key={s.step} className="text-center">
                  <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center mx-auto mb-2`}>
                    <span className={`text-sm font-bold ${s.color}`}>{s.step}</span>
                  </div>
                  <p className="text-xs font-semibold text-white mb-0.5">{s.label}</p>
                  <p className="text-[10px] text-zinc-600 leading-snug">{s.desc}</p>
                  {i < 4 && <ArrowRight className="w-3 h-3 text-zinc-800 mx-auto mt-2 hidden sm:block" />}
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* PROBLEM */}
        <section data-testid="problem-section">
          <SectionHeader tag="The Problem" title="Enterprise Meetings Are a Black Hole" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: '73%', t: 'of action items from meetings are never followed up on' },
              { n: '$37B', t: 'lost annually to unproductive meetings in the US alone' },
              { n: '4.2hrs', t: 'per week spent by managers reconciling meeting outcomes' },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="p-5 rounded-xl bg-red-500/[0.04] border border-red-500/10 text-center"
              >
                <p className="text-2xl font-extrabold text-red-400">{s.n}</p>
                <p className="text-xs text-zinc-500 mt-1">{s.t}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FEATURE READINESS MAP */}
        <section data-testid="feature-map-section" className="print-break">
          <SectionHeader
            tag="Feature Readiness Map"
            title="Every Feature is Live & Production-Tested"
            sub="Not wireframes or mockups. Every capability listed below is fully implemented, tested, and running in the live product."
          />

          {/* AI & Core Intelligence */}
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-indigo-400 mb-3">AI & Core Intelligence</p>
            <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FeatureRow icon={Brain} title="AI Task Extraction" desc="GPT-powered extraction of tasks, owners, deadlines, and priorities from meeting transcripts" color="bg-indigo-500/20" />
              <FeatureRow icon={AlertTriangle} title="AI Risk Identification" desc="Automatic detection of blockers, compliance gaps, and concerns from discussions" color="bg-amber-500/20" />
              <FeatureRow icon={BarChart3} title="AI Analytics & Insights" desc="Productivity patterns, completion trends, and AI-generated recommendations" color="bg-emerald-500/20" />
              <FeatureRow icon={Mic} title="Product Tour Voiceover" desc="AI-generated voiceovers cached as MP3s for guided product walkthroughs" color="bg-violet-500/20" />
            </motion.div>
          </div>

          {/* Collaboration & Real-Time */}
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-blue-400 mb-3">Collaboration & Real-Time</p>
            <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FeatureRow icon={Users} title="Real-Time Presence" desc="WebSocket-powered live user indicators showing who's viewing what" color="bg-blue-500/20" />
              <FeatureRow icon={MessageSquare} title="Comments & @Mentions" desc="Inline commenting with @mention notifications via email" color="bg-cyan-500/20" />
              <FeatureRow icon={Mail} title="Email Reply-to-Comment" desc="SendGrid Inbound Parse auto-logs email replies as task/risk comments" color="bg-teal-500/20" />
              <FeatureRow icon={Bell} title="Smart Notifications" desc="In-app notification center with deep-linked email alerts" color="bg-sky-500/20" />
            </motion.div>
          </div>

          {/* Enterprise & Security */}
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-400 mb-3">Enterprise & Security</p>
            <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FeatureRow icon={Shield} title="Role-Based Access Control" desc="Admin, Manager, Member roles with granular permission gating" color="bg-emerald-500/20" />
              <FeatureRow icon={Lock} title="Auth Hardening" desc="JWT + bcrypt, brute-force protection, Redis rate limiting, password reset" color="bg-green-500/20" />
              <FeatureRow icon={FileText} title="Full Audit Logging" desc="Every action tracked with timestamps, exportable audit trails" color="bg-lime-500/20" />
              <FeatureRow icon={Globe} title="Multi-Tenant Architecture" desc="Organization isolation, hostname-based routing, secure data separation" color="bg-emerald-600/20" />
            </motion.div>
          </div>

          {/* Integrations & Billing */}
          <div className="mb-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-violet-400 mb-3">Integrations & Revenue</p>
            <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FeatureRow icon={Video} title="Meeting Platform OAuth" desc="Native Zoom, Google Meet, and Microsoft Teams OAuth integrations" color="bg-violet-500/20" />
              <FeatureRow icon={CreditCard} title="Stripe Billing" desc="4-tier subscription system (Free, Starter, Growth, Enterprise) with feature gating" color="bg-pink-500/20" />
              <FeatureRow icon={Mail} title="SendGrid Email System" desc="Transactional emails, event webhooks, open/click tracking, engagement dashboard" color="bg-rose-500/20" />
              <FeatureRow icon={RefreshCw} title="Webhook Infrastructure" desc="SendGrid event/inbound webhooks, Stripe billing webhooks" color="bg-fuchsia-500/20" />
            </motion.div>
          </div>

          {/* GTM & Onboarding */}
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-amber-400 mb-3">Go-to-Market Readiness</p>
            <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <FeatureRow icon={Play} title="Interactive Demo Mode" desc="Self-serve demo with simulated real-time data, guided product tour with voiceover" color="bg-amber-500/20" />
              <FeatureRow icon={UserPlus} title="First-Time Onboarding" desc="Step-by-step guided setup flow with progress tracking and completion emails" color="bg-orange-500/20" />
              <FeatureRow icon={MonitorSmartphone} title="Marketing Landing Page" desc="Standalone conversion-optimized landing page with demo request form" color="bg-yellow-500/20" />
              <FeatureRow icon={Eye} title="Email Engagement Dashboard" desc="Admin view of email opens, clicks, CTA engagement rates by email type" color="bg-amber-600/20" />
            </motion.div>
          </div>
        </section>

        {/* TECHNICAL ARCHITECTURE */}
        <section className="print-break" data-testid="tech-section">
          <SectionHeader
            tag="Technical Architecture"
            title="Production-Ready From Day One"
            sub="Not a prototype — a fully hardened SaaS platform built for enterprise deployment."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div {...fadeUp} className="p-6 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-400" /> Stack
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ['Frontend', 'React + Tailwind + Framer Motion'],
                  ['Backend', 'FastAPI (Python) + Motor'],
                  ['Database', 'MongoDB (multi-tenant)'],
                  ['Cache', 'Redis (rate limiting, sessions)'],
                  ['AI', 'OpenAI GPT + TTS via abstraction'],
                  ['Payments', 'Stripe (4 tiers + enterprise)'],
                  ['Email', 'SendGrid (transactional + webhooks)'],
                  ['Real-Time', 'WebSocket (presence + collab)'],
                  ['Auth', 'JWT + bcrypt + rate limiting'],
                  ['Integrations', 'Zoom, Google, Teams OAuth'],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-zinc-600 font-medium w-20 shrink-0">{k}</span>
                    <span className="text-zinc-400">{v}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="p-6 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Cpu className="w-4 h-4 text-emerald-400" /> Architecture Highlights
              </h3>
              <ul className="space-y-2">
                {[
                  'Modular backend — domain-separated route files (tasks, risks, meetings)',
                  'Multi-tenant data isolation with org-scoped queries',
                  'Redis-backed persistent rate limiting (survives restarts)',
                  'WebSocket connection management with presence tracking',
                  'SendGrid Inbound Parse for email-to-comment pipeline',
                  'Subscription-gated feature access (tiered enforcement)',
                  'Structured JSON logging with request tracing',
                  'Automated demo data seeding (idempotent)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* REVENUE MODEL */}
        <section data-testid="revenue-section">
          <SectionHeader
            tag="Revenue Model"
            title="Proven SaaS Pricing Infrastructure"
            sub="Three self-serve tiers plus enterprise contracts. Full Stripe billing with usage gating already implemented."
          />
          <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'Starter', price: '$29', period: '/mo', features: ['5 meetings/mo', '10 tasks', 'Basic analytics', 'Email support'], color: 'border-zinc-700' },
              { name: 'Growth', price: '$79', period: '/mo', features: ['50 meetings/mo', 'Unlimited tasks', 'AI insights', 'All integrations', 'Priority support'], color: 'border-indigo-500/40', popular: true },
              { name: 'Enterprise', price: 'Custom', period: '', features: ['Unlimited everything', 'SSO/SAML', 'Dedicated success manager', 'Custom integrations', 'SLA guarantee'], color: 'border-violet-500/30' },
            ].map((plan) => (
              <motion.div
                key={plan.name}
                variants={cardV}
                className={`p-6 rounded-xl bg-white/[0.02] border ${plan.color} relative`}
              >
                {plan.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-3 py-0.5 rounded-full">
                    Target Tier
                  </span>
                )}
                <p className="text-sm font-semibold text-white">{plan.name}</p>
                <p className="mt-2 mb-4">
                  <span className="text-2xl font-extrabold text-white">{plan.price}</span>
                  <span className="text-xs text-zinc-600">{plan.period}</span>
                </p>
                <ul className="space-y-2">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-zinc-400">
                      <CheckCircle className="w-3 h-3 text-indigo-500 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
          <motion.p {...fadeUp} className="text-[11px] text-zinc-600 mt-4 text-center">
            All pricing tiers are fully wired to Stripe with subscription lifecycle management, webhooks, and feature gating.
          </motion.p>
        </section>

        {/* MARKET OPPORTUNITY */}
        <section className="print-break" data-testid="market-section">
          <SectionHeader
            tag="Market"
            title="Massive & Growing Market"
            sub="Meeting intelligence sits at the intersection of multiple high-growth enterprise software categories."
          />
          <motion.div {...stagger} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: '$26B', l: 'Meeting Management TAM', sub: 'by 2028' },
              { v: '$18B', l: 'AI Enterprise Tools', sub: 'CAGR 34%' },
              { v: '300M+', l: 'Daily Meeting Participants', sub: 'globally' },
              { v: '62%', l: 'Enterprises Adopting AI', sub: 'for workflows by 2027' },
            ].map((s, i) => (
              <motion.div key={i} variants={cardV} className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center">
                <p className="text-xl font-extrabold text-indigo-400">{s.v}</p>
                <p className="text-xs text-zinc-400 mt-1 font-medium">{s.l}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">{s.sub}</p>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* COMPETITIVE LANDSCAPE */}
        <section data-testid="competitive-section">
          <SectionHeader
            tag="Competitive Edge"
            title="Beyond Meeting Transcription"
            sub="Most tools stop at transcription. D3 Command delivers execution, risk management, and organizational intelligence."
          />
          <motion.div {...fadeUp} className="overflow-hidden rounded-xl border border-white/[0.06]">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="px-5 py-3 text-zinc-500 font-medium uppercase tracking-wider">Capability</th>
                  <th className="px-5 py-3 text-center text-indigo-400 font-bold uppercase tracking-wider">D3 Command</th>
                  <th className="px-5 py-3 text-center text-zinc-600 font-medium uppercase tracking-wider">Otter.ai</th>
                  <th className="px-5 py-3 text-center text-zinc-600 font-medium uppercase tracking-wider">Fireflies</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Meeting Transcription', true, true, true],
                  ['AI Task Extraction & Tracking', true, false, true],
                  ['Risk Identification & Management', true, false, false],
                  ['Real-Time Collaborative Presence', true, false, false],
                  ['@Mention Notifications', true, false, true],
                  ['Email Reply-to-Comment Pipeline', true, false, false],
                  ['Multi-Tenant Enterprise RBAC', true, false, false],
                  ['Full Audit Logging & Compliance', true, false, false],
                  ['Stripe Billing + Feature Gating', true, true, true],
                  ['Interactive Sales Demo Mode', true, false, false],
                  ['AI-Powered Product Tour Voiceover', true, false, false],
                  ['Guided Onboarding + Lifecycle Emails', true, false, true],
                ].map(([feature, d3, otter, ff], i) => (
                  <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.01]">
                    <td className="px-5 py-2.5 text-zinc-400">{feature}</td>
                    {[d3, otter, ff].map((v, j) => (
                      <td key={j} className="px-5 py-2.5 text-center">
                        {v
                          ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          : <span className="text-zinc-700">&mdash;</span>
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="print-break" data-testid="included-section">
          <SectionHeader tag="Acquisition" title="What's Included" />
          <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              'Full source code (React + FastAPI + MongoDB)',
              'All intellectual property and branding assets',
              'Domain names (d3command.com, app.d3command.com)',
              'Stripe account with configured billing plans',
              'SendGrid account with email templates & analytics',
              'Zoom, Google, Teams OAuth app credentials',
              'MongoDB database with all schemas & seed data',
              'Redis configuration for production rate limiting',
              'Deployment infrastructure and CI/CD pipeline',
              'Complete technical documentation',
              'WebSocket infrastructure for real-time features',
              'Transition support (30-day handoff period)',
            ].map((item, i) => (
              <motion.div key={i} variants={cardV} className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-xs text-zinc-400">{item}</span>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CTA */}
        <section className="relative" data-testid="cta-section">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-600/5 to-transparent rounded-2xl pointer-events-none" />
          <motion.div
            {...fadeUp}
            className="relative text-center py-14 px-6 rounded-2xl border border-white/[0.06]"
          >
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Interested in Acquiring D3 Command?
            </h2>
            <p className="text-sm text-zinc-400 max-w-lg mx-auto mb-8">
              We're open to conversations with strategic acquirers, PE firms, and investors
              who see the value in enterprise operations and risk intelligence.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href="/login?demo=true"
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-500 transition-colors"
                data-testid="cta-demo"
              >
                <Play className="w-4 h-4" />
                Try the Live Demo
              </a>
              <a
                href="mailto:invest@d3command.com"
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-500 transition-colors"
                data-testid="cta-email"
              >
                <Mail className="w-4 h-4" />
                invest@d3command.com
              </a>
              <a
                href="https://calendly.com/d3command-support/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
                data-testid="cta-schedule"
              >
                <Calendar className="w-4 h-4" />
                Schedule a Call
              </a>
            </div>
          </motion.div>
        </section>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12 no-print">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <Logo h={24} />
          <p className="text-[10px] text-zinc-700 text-center sm:text-right max-w-md">
            Confidential — For Prospective Acquirers Only
          </p>
        </div>
      </footer>
    </div>
  );
}
